import { isStaleChunkError } from "./is-stale-chunk-error";
import { createSeenRegistry } from "./seen-registry";

/** Shared with vision-ai-v2's existing marker, so a tab already holding a spent fuse keeps it. */
export const RELOAD_MARKER_KEY = "pandas.lazyChunkRetry.reloadSpent";
export const UNKNOWN_DEPLOYMENT = "unknown-deploy";
/** A recovery's row, held across the reload: the unloading page would drop its unsent log batch. */
export const DEFERRED_FAILURE_KEY = "pandas.lazyChunkRetry.deferredFailure";
/** A page still alive this long after asking to reload had the reload vetoed (a leave prompt) or ignored. */
export const RELOAD_SURVIVAL_MS = 5000;

export type ChunkFailureReason = "import" | "route_chunk" | "css_preload";

export type ChunkFailure = {
	error: unknown;
	chunkName: string;
	reason: ChunkFailureReason;
	/** True when this deployment's one automatic reload was already used. */
	reloadSpent: boolean;
	/** True when the page that booted after the recovery's reload reports it, which also proves that page loaded. */
	deferred: boolean;
	/** True when the failure happened offline: nothing was reloaded and the fuse is untouched. */
	offline: boolean;
	/** The deployment whose chunk failed; set on recovery rows, which another deployment's page may report. */
	failedDeploymentId?: string;
	/** One id per recovery, stable across replays, so an at-least-once replay can be deduplicated. */
	recoveryId?: string;
};

export type ReloadMarkerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type PreloadErrorEvent = { payload?: unknown; preventDefault(): void };

export type PreloadErrorTarget = {
	addEventListener(type: "vite:preloadError", listener: (event: PreloadErrorEvent) => void): void;
	removeEventListener(type: "vite:preloadError", listener: (event: PreloadErrorEvent) => void): void;
};

export type StaleChunkRecoveryOptions = {
	/** Re-read per call; "" falls back to the placeholder, so an unidentified build gets one reload per tab. */
	deploymentId: () => string;
	onChunkFailure: (failure: ChunkFailure) => void;
	/** Sends the app's pending log batch; the replayed row's marker is kept until it settles. */
	flush?: () => Promise<void>;
	storage?: () => ReloadMarkerStorage;
	reload?: () => void;
	/** Defaults to `navigator.onLine !== false`; a reload offline only lands on the browser's error page. */
	isOnline?: () => boolean;
};

export type StaleChunkRecovery = {
	reloadMarkerValue(): string;
	/** True between a recovery's reload request and the page unloading; a leave guard should let it through. */
	isReloading(): boolean;
	maybeRecoverChunkCrash(error: unknown): boolean;
	withLazyChunkRetry<T>(load: () => Promise<T>, chunkName: string): () => Promise<T>;
	startChunkPreloadRecovery(win?: PreloadErrorTarget): () => void;
};

type DeferredFailure = {
	chunkName: string;
	reason: ChunkFailureReason;
	errorName: string;
	failedDeploymentId: string;
	recoveryId: string;
};

export function createStaleChunkRecovery(options: StaleChunkRecoveryOptions): StaleChunkRecovery {
	const storage = options.storage ?? (() => window.sessionStorage);
	const reload = options.reload ?? (() => window.location.reload());
	const isOnline = options.isOnline ?? (() => navigator.onLine !== false);
	const loggedErrors = createSeenRegistry();
	const handledCrashErrors = createSeenRegistry();
	let reloading = false;

	const reloadMarkerValue = () => options.deploymentId() || UNKNOWN_DEPLOYMENT;

	function hasSpentReload(): boolean {
		try {
			return storage().getItem(RELOAD_MARKER_KEY) === reloadMarkerValue();
		} catch {
			// Unreadable storage counts as spent: it is the only guard against an unbounded reload loop.
			return true;
		}
	}

	function markReloadSpent(): boolean {
		try {
			const store = storage();
			store.setItem(RELOAD_MARKER_KEY, reloadMarkerValue());
			return store.getItem(RELOAD_MARKER_KEY) === reloadMarkerValue();
		} catch {
			return false;
		}
	}

	function readDeferred(): DeferredFailure | null {
		try {
			const raw = storage().getItem(DEFERRED_FAILURE_KEY);
			const stored: unknown = raw === null ? null : JSON.parse(raw);
			return isDeferredFailure(stored) ? stored : null;
		} catch {
			return null;
		}
	}

	/** Clears the marker only if it still holds this recovery, so a newer recovery's row is never dropped. */
	function clearDeferred(recoveryId: string | null): void {
		try {
			if (recoveryId === null || readDeferred()?.recoveryId === recoveryId) {
				storage().removeItem(DEFERRED_FAILURE_KEY);
			}
		} catch {
			// Nothing to clean up when storage is unavailable.
		}
	}

	function deferFailure(error: unknown, chunkName: string, reason: ChunkFailureReason): string | null {
		const deferred: DeferredFailure = {
			chunkName,
			reason,
			errorName: errorNameOf(error),
			failedDeploymentId: reloadMarkerValue(),
			recoveryId: Math.random().toString(36).slice(2, 12),
		};
		try {
			storage().setItem(DEFERRED_FAILURE_KEY, JSON.stringify(deferred));
			return deferred.recoveryId;
		} catch {
			return null;
		}
	}

	function reportDeferred(stored: DeferredFailure, deferred: boolean): void {
		const error = new Error("deferred stale-chunk failure");
		error.name = stored.errorName;
		options.onChunkFailure({
			error,
			chunkName: stored.chunkName,
			reason: stored.reason,
			reloadSpent: false,
			deferred,
			offline: false,
			failedDeploymentId: stored.failedDeploymentId,
			recoveryId: stored.recoveryId,
		});
	}

	/** At-least-once: the marker survives until the flush settles, so a reload before that replays the row. */
	function replayDeferredFailure(): void {
		const stored = readDeferred();
		if (stored === null) {
			clearDeferred(null);
			return;
		}
		reportDeferred(stored, true);
		const { flush } = options;
		if (!flush) {
			clearDeferred(stored.recoveryId);
			return;
		}
		flush()
			.catch(() => undefined)
			.finally(() => clearDeferred(stored.recoveryId));
	}

	/** A vetoed reload leaves the page alive with its row parked, so the row is logged here instead. */
	function watchReloadSurvival(recoveryId: string | null): void {
		setTimeout(() => {
			if (!reloading) {
				return;
			}
			reloading = false;
			const stored = readDeferred();
			if (recoveryId !== null && stored?.recoveryId === recoveryId) {
				reportDeferred(stored, false);
				clearDeferred(recoveryId);
			}
		}, RELOAD_SURVIVAL_MS);
	}

	/** Logs once and spends the fuse; true only when a reload was started, otherwise the caller surfaces the error. Offline it neither reloads nor spends the fuse. */
	function recover(error: unknown, chunkName: string, reason: ChunkFailureReason, logWhenSpent = true): boolean {
		const reloadSpent = hasSpentReload();
		const firstSighting = !(reloadSpent && !logWhenSpent) && !loggedErrors.seen(error);
		const offline = !isOnline();
		const logLive = () => {
			if (firstSighting) {
				options.onChunkFailure({ error, chunkName, reason, reloadSpent, deferred: false, offline });
			}
		};
		if (offline) {
			// shortcut: a connection that drops mid-download still reaches TanStack's own route reload, upgrade when the router exposes a hook to veto it.
			logLive();
			return false;
		}
		if (reloadSpent || !markReloadSpent()) {
			logLive();
			return false;
		}
		// A row logged now would die with this document's log batch, so the next page reports it.
		const recoveryId = firstSighting ? deferFailure(error, chunkName, reason) : null;
		if (firstSighting && recoveryId === null) {
			logLive();
		}
		reloading = true;
		watchReloadSurvival(recoveryId);
		reload();
		return true;
	}

	return {
		reloadMarkerValue,
		isReloading: () => reloading,
		maybeRecoverChunkCrash(error) {
			if (!isStaleChunkError(error)) {
				return false;
			}
			if (!handledCrashErrors.seen(error)) {
				recover(error, "route-chunk", "route_chunk");
			}
			return true;
		},
		withLazyChunkRetry<T>(load: () => Promise<T>, chunkName: string) {
			return () =>
				load().catch((error: unknown): Promise<T> => {
					// An import that fails for any reason other than a stale chunk must surface, not spend the reload mid-flow.
					if (!isStaleChunkError(error) || !recover(error, chunkName, "import")) {
						throw error;
					}
					return new Promise<T>(() => {});
				});
		},
		startChunkPreloadRecovery(win = window) {
			replayDeferredFailure();
			const onPreloadError = (event: PreloadErrorEvent) => {
				// Vite routes every rejected import here, so only a stale-chunk failure is recovered. On a spent
				// budget the import that triggered the preload logs it instead.
				if (isStaleChunkError(event.payload) && recover(event.payload, "vite-preload", "css_preload", false)) {
					event.preventDefault();
				}
			};
			win.addEventListener("vite:preloadError", onPreloadError);
			return () => win.removeEventListener("vite:preloadError", onPreloadError);
		},
	};
}

/** Matches `@pandas/logger`'s fallback name, so live and deferred rows agree for a thrown non-Error. */
function errorNameOf(error: unknown): string {
	return error instanceof Error ? error.name : "UnknownError";
}

const CHUNK_FAILURE_REASONS: readonly string[] = [
	"import",
	"route_chunk",
	"css_preload",
] satisfies ChunkFailureReason[];

function isDeferredFailure(value: unknown): value is DeferredFailure {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	const { chunkName, reason, errorName, failedDeploymentId, recoveryId } = value as Record<string, unknown>;
	return (
		typeof chunkName === "string" &&
		typeof errorName === "string" &&
		typeof failedDeploymentId === "string" &&
		typeof recoveryId === "string" &&
		typeof reason === "string" &&
		CHUNK_FAILURE_REASONS.includes(reason)
	);
}
